// feedbackForm — 「タカミン窓口」フィードバックフォームの純粋ロジック.
//
// 背景: launch-rollout-plan.md §3.3 / §3.3.7 (= 2026-05-23 W2 投入分).
// フォーム UI (= FeedbackButton.tsx) から呼ばれる純粋関数群:
//   - `validateFeedbackMessage` — 1..5000 chars + trim 後の空文字弾き. protocol
//     schema (= `feedbackSubmitSchema`) と同条件で client-side に出すエラーを
//     決める. trim 後 0 文字は schema 上 `min(1)` を通過してしまうので、UI 側で
//     先に弾く (= サーバ rejected の `message-empty` を待たない).
//   - `mapFeedbackRejectedReason` — server からの `feedback.rejected.reason`
//     を日本語 UI 文言に変換. 仕様文言は launch-rollout-plan.md §3.3.7 と
//     frontend-developer agent prompt から踏襲.
//
// 純粋関数として `application/` に分離する理由:
//   - React 環境 (jsdom) 不要 vitest (= node env) でカバレッジを稼げる.
//   - 文言を 1 箇所に集約 (= UI コンポーネント側で重複させない).
//   - i18n 拡張 / 文言変更時の影響範囲を局所化.
//
// MVP スコープ:
//   - 種別 (= バグ報告 / 要望 / その他) は付けない (§3.3.4 却下案).
//   - コンテキスト自動添付 (UA / undo log / viewport) は後追い (§3.3.7).
//     UI 側からは `message` + optional `roomId` のみ submit する.

import type { FeedbackRejected } from '@oekaki/protocol';

/** protocol schema (`feedbackSubmitSchema.message`) と同じ上限. */
export const FEEDBACK_MESSAGE_MAX_LENGTH = 5000;

/**
 * 入力バリデーション結果. UI は `outcome === 'invalid'` のとき送信ボタンを
 * disable し、`message` をエラー表示として使う.
 */
export type ValidateFeedbackMessageOutcome =
  | { readonly outcome: 'ok'; readonly trimmed: string }
  | { readonly outcome: 'empty' }
  | { readonly outcome: 'too-long'; readonly max: number };

/**
 * フォーム submit 前にクライアント側で 1 回 validate する.
 *
 * `feedbackSubmitSchema` は `message: z.string().min(1).max(5000)`. min(1) は
 * trim 前の生長さを見るため、サーバが `message-empty` を返す可能性は残る
 * (= 空白だけの文字列). UI は trim 後で先に弾いて「サーバに無駄リクエスト
 * + 入力欄が flush される rejected 待ち時間」を発生させない.
 *
 * - `trimmed` は protocol 側に流す確定文字列. caller はそのまま payload に
 *   入れる (= 末尾改行や全角空白はサーバ側保存物に残らない方が運用しやすい).
 * - 上限超過は protocol schema 通り 5000 文字を境界に判定 (= 5001 で fail).
 *   超過時の `message-too-long` は trim 前の長さで判断する (= サーバ schema
 *   と同条件、trim 後ぴったり 5000 でも raw が 5001 なら超過扱い).
 *   …という厳格運用にはせず、UX 重視で **trim 後の長さ** で判定する.
 *   `feedbackSubmitSchema.parse(trimmed)` を最終的に通すので、trim 後が
 *   5000 文字以内なら server も accept する.
 */
export const validateFeedbackMessage = (raw: string): ValidateFeedbackMessageOutcome => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { outcome: 'empty' };
  if (trimmed.length > FEEDBACK_MESSAGE_MAX_LENGTH) {
    return { outcome: 'too-long', max: FEEDBACK_MESSAGE_MAX_LENGTH };
  }
  return { outcome: 'ok', trimmed };
};

/**
 * server から届く `feedback.rejected.reason` を日本語 UI 文言に変換.
 *
 * 文言は launch-rollout-plan.md §3.3.7 + frontend-developer agent prompt から:
 *   - rate-limited:    「短時間に多くのご投稿をいただいたため…」 (= 同一 uid /
 *                       24h で 5 件まで)
 *   - message-too-long: 「メッセージが長すぎます (5000 文字以内…)」
 *   - message-empty:   「メッセージを入力してください」
 *
 * server 側 `feedback.rejected` で `message` (= optional) が乗っている場合は
 * 上書きしない (= 上記 default が常に優先). server が独自文言を返す経路は
 * 現状無いが、protocol が optional で持っているので将来の拡張余地は残る.
 */
export const mapFeedbackRejectedReason = (reason: FeedbackRejected['reason']): string => {
  switch (reason) {
    case 'rate-limited':
      return '短時間に多くのご投稿をいただいたため、少し時間をおいてからお試しください (24 時間以内に 5 件まで)';
    case 'message-too-long':
      return `メッセージが長すぎます (${FEEDBACK_MESSAGE_MAX_LENGTH} 文字以内でお願いします)`;
    case 'message-empty':
      return 'メッセージを入力してください';
  }
};

/**
 * 入力バリデーション結果 → UI に出すエラー文言. `outcome === 'ok'` は null
 * (= エラーなし). UI は `null` 時に送信ボタンを enable する.
 */
export const validateFeedbackMessageErrorText = (
  outcome: ValidateFeedbackMessageOutcome,
): string | null => {
  switch (outcome.outcome) {
    case 'ok':
      return null;
    case 'empty':
      // フォーカス時 / 入力前は空欄 (= disable 状態) なので「エラー」として
      // 表示はせず、submit 試行時にだけ呼び出し側で出す想定.
      return 'メッセージを入力してください';
    case 'too-long':
      return `メッセージが長すぎます (${outcome.max} 文字以内でお願いします)`;
  }
};
